/**
 * useWmsProfile — Read the active WMS warehouse profile from LayoutConfig.
 *
 * Priority chain:
 *   1. explicitTemplateCode (if caller provides one to the resolver)
 *   2. warehouseTemplateAssignments
 *   3. warehouseProfileTemplateCode
 *   4. warehouseProfile
 *   5. Fallback to 'hybrid'
 *
 * Usage:
 *   const { profile, profileKey } = useWmsProfile();
 *   const { templateCode } = useWmsProfile({ warehouseCode: 'WH-1' });
 *   // profile.kpis, profile.flowSteps, profile.theme, profile.visibleNavIds
 */

import { useMemo } from 'react';
import { useLayoutConfigContext } from '@/lib/use-layout-config';
import { resolveWmsTemplateSelection } from '@/lib/wms-template-config';
import type { WmsProfile, WmsProfileKey } from './wms-profile-config';
import type {
  ResolvedWmsTemplateSelection,
  WmsTemplateCode,
  WmsTemplateResolutionContext,
} from '@/lib/wms-template-config';

export interface UseWmsProfileResult {
  profileKey: WmsProfileKey;
  profile: WmsProfile;
  templateCode: WmsTemplateCode;
  template: ResolvedWmsTemplateSelection['template'];
  templateSource: ResolvedWmsTemplateSelection['source'];
  matchedAssignment: ResolvedWmsTemplateSelection['matchedAssignment'];
  assignments: ResolvedWmsTemplateSelection['assignments'];
}

export function useWmsProfile(context?: WmsTemplateResolutionContext): UseWmsProfileResult {
  const { config } = useLayoutConfigContext();

  return useMemo(() => {
    const resolved = resolveWmsTemplateSelection({
      warehouseProfile: config?.warehouseProfile,
      warehouseProfileTemplateCode: config?.warehouseProfileTemplateCode,
      warehouseTemplateAssignments: config?.warehouseTemplateAssignments,
    }, context);

    return {
      profileKey: resolved.profileKey,
      profile: resolved.profile,
      templateCode: resolved.templateCode,
      template: resolved.template,
      templateSource: resolved.source,
      matchedAssignment: resolved.matchedAssignment,
      assignments: resolved.assignments,
    };
  }, [
    config?.warehouseProfile,
    config?.warehouseProfileTemplateCode,
    config?.warehouseTemplateAssignments,
    context?.explicitTemplateCode,
    context?.warehouseCode,
    context?.customerCode,
    context?.operationType,
    context?.jobType,
  ]);
}
